/**
 * Simulations Intro Page Component
 * Gateway to all available interactive simulation tools
 * @module SimulationsIntro
 */

import React from 'react';
import { Typography, Button, Tag } from 'antd';
import { 
  ControlOutlined,
  DeploymentUnitOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Intro.module.css';
import PagesWrapper from '../../../PagesWrapper';

const { Title, Paragraph } = Typography;

/**
 * Simulations Intro Page
 * Displays available and upcoming simulation tools
 */
const SimulationsIntro = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  /**
   * Available and upcoming simulations
   */
  const simulations = [
    {
      id: 'heat-pump-simulation',
      title: t('simulations.heatPump.title'),
      icon: <ControlOutlined />,
      features: [
        t('simulations.heatPump.features.feature1'),
        t('simulations.heatPump.features.feature2'),
        t('simulations.heatPump.features.feature3'),
        t('simulations.heatPump.features.feature4')
      ],
      status: 'available',
      link: '/simulations/heat-pump-simulation',
      color: '#1890ff'
    },
    {
      id: 'hydraulic-balancing',
      title: t('simulations.hydraulicBalancing.title'),
      icon: <ControlOutlined />,
      features: [
        t('simulations.hydraulicBalancing.features.feature1'),
        t('simulations.hydraulicBalancing.features.feature2'),
        t('simulations.hydraulicBalancing.features.feature3'),
        t('simulations.hydraulicBalancing.features.feature4')
      ],
      status: 'available',
      link: '/simulations/hydraulic-balancing',
      color: '#fa8c16'
    },
    {
      id: 'hybrid-system-sim',
      title: t('simulations.hybridSystem.title'),
      icon: <DeploymentUnitOutlined />,
      features: [
        t('simulations.hybridSystem.features.feature1'),
        t('simulations.hybridSystem.features.feature2'),
        t('simulations.hybridSystem.features.feature3'),
        t('simulations.hybridSystem.features.feature4')
      ],
      status: 'coming-soon',
      link: null,
      color: '#13c2c2'
    }
  ];

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <PagesWrapper>
      <div className={styles.introContainer}>
        
        {/* Header */}
        <div className={styles.header}>
          <Title level={1}>{t('simulations.introTitle')}</Title>
          <Paragraph>{t('simulations.introSubtitle')}</Paragraph>
        </div>

        {/* Simulations List */}
        <div className={styles.simulationsList}>
          {simulations.map((sim) => (
            <div key={sim.id} className={styles.simulationItem}>
              <div className={styles.itemIcon} style={{ color: sim.color }}>
                {sim.icon}
              </div>
              
              <div className={styles.itemContent}>
                <div className={styles.itemHeader}>
                  <Title level={4} className={styles.itemTitle}>{sim.title}</Title>
                  {sim.status !== 'available' && (
                    <Tag className={styles.comingSoonTag}>
                      <LockOutlined style={{ marginRight: 6 }} />
                      {t('simulations.comingSoon')}
                    </Tag>
                  )}
                </div>

                <ul className={styles.featuresList}>
                  {sim.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.itemButton}>
                <Button 
                  type={sim.status === 'available' ? 'primary' : 'default'}
                  disabled={sim.status === 'coming-soon'}
                  onClick={() => handleNavigate(sim.link)}
                >
                  {sim.status === 'available' ? t('simulations.launchSimulator') : t('simulations.comingSoon')}
                </Button>
              </div>
            </div>
          ))}
        </div>

      </div>
    </PagesWrapper>
  );
};

export default SimulationsIntro;
